// src/componets/navigation/Navbar.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { PlusCircle, RefreshCw } from "lucide-react";
import { Button } from "@radix-ui/themes";
import { useSupaContext } from "../../contexts/SupaContext";
import Sidepanel from "./Sidepanel";

// Hook personalizado para manejar el estado del navbar
const useNavbarState = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSidebar, setActiveSidebar] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const openSidebar = (type) => setActiveSidebar(type);
  const closeSidebar = () => setActiveSidebar(null);

  return {
    isScrolled,
    activeSidebar,
    openSidebar,
    closeSidebar,
  };
};

// Componente para el logo
const NavbarLogo = ({ onClick, className = "" }) => (
  <div className={`flex items-center ${className}`} onClick={onClick}>
    <img
      src="/assets/logos/fluenti-logo-black-140.svg"
      className="h-16 md:h-16 cursor-pointer transition-transform hover:scale-105"
      alt="Fluenti Logo"
    />
  </div>
);

// Componente para enlaces de navegación
const NavLink = ({ children, onClick, isScrolled, className = "", route }) => (
  <a
    href={route}
    className={`
      px-3 py-2 rounded-md font-regular text-base transition-colors
      ${
        isScrolled
          ? "text-gray-800 hover:text-blue-600"
          : "text-white hover:text-blue-100"
      }
      ${className}
    `}
  >
    {children}
  </a>
);

// Navbar principal (tipo general)
const GeneralNavbar = ({ isScrolled, openSidebar }) => {
  const navigate = useNavigate();
  const { user } = useSupaContext();

  const navItems = [
    {
      label: "Inicio",
      type: "sidebar",
      route: "/",
    },
    {
      label: "Cobertura",
      type: "sidebar",
      route: "/cobertura",
    },
  ];

  return (
    <div className="flex items-center justify-between w-full max-w-6xl mx-auto px-4 xl:px-0">
      <a href="/" className="flex items-center">
        <NavbarLogo />
      </a>

      <div className="flex items-center gap-5">
        {/* Enlaces de navegación - Solo desktop */}
        <div className="hidden md:flex items-center gap-4">
          {navItems.map((item, index) => (
            <NavLink key={index} route={item.route} isScrolled={isScrolled}>
              {item.label}
            </NavLink>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <a
            className="transform transition-transform duration-200 font-medium bg-stone-50 text-stone-800 rounded-xl hidden md:flex items-center gap-2 px-3 py-2   hover:scale-105"
            href="/nuevo-pedido"
          >
            <PlusCircle size={16} />
            Pedir botellón
          </a>
          {/* Botón de usuario */}
          {user ? (
            <a
              className="transform transition-transform duration-200 font-medium bg-stone-800 text-stone-50 flex items-center gap-2 px-3 py-2 rounded-xl  hover:bg-stone-900 hover:scale-105"
              href="/app"
            >
              Mis botellones
            </a>
          ) : (
            <a
              className="transform transition-transform duration-200 font-medium bg-stone-800 text-stone-50 flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-gray-900 hover:scale-105"
              href="/ingresar"
            >
              Ingresar
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// Navbar para login
const LoginNavbar = () => {
  const navigate = useNavigate();

  return (
    <div className="w-full max-w-6xl mx-auto px-4 xl:px-0">
      <div className="flex items-center justify-between h-16">
        <NavbarLogo onClick={() => navigate("/")} />

        <Button
          size="3"
          variant="surface"
          onClick={() => navigate("/registro")}
          className="transition-colors"
        >
          Crea tu cuenta
        </Button>
      </div>
    </div>
  );
};

// Navbar para nuevo pedido
const NewOrderNavbar = () => {
  const navigate = useNavigate();

  return (
    <div className="flex items-center justify-between w-full max-w-6xl mx-auto px-4 xl:px-0">
      <NavbarLogo onClick={() => navigate("/")} />

      <h1 className="text-xl md:text-2xl font-normal text-gray-900">
        Nuevo pedido
      </h1>
    </div>
  );
};
// Navbar para nuevo pedido
const NewRefillsNavbar = () => {
  const navigate = useNavigate();

  return (
    <div className="flex items-center justify-between w-full max-w-6xl mx-auto px-4 xl:px-0">
      <NavbarLogo onClick={() => navigate("/")} />

      <h1 className="text-xl md:text-2xl font-normal text-gray-900">
        Pedir recambios
      </h1>
    </div>
  );
};

// Componente principal del Navbar
const Navbar = ({
  type = "general",
  staticPosition = false,
  className = "",
}) => {
  const { isScrolled, activeSidebar, openSidebar, closeSidebar } =
    useNavbarState();
  const location = useLocation();

  // Determinar el z-index apropiado
  const getZIndex = () => {
    // Jerarquía de z-index definida:
    // - Modales críticos: z-[100]
    // - Navbar: z-[90] (siempre por encima del contenido)
    // - Sidepanels: z-[80]
    // - TotalBar y elementos fijos: z-[70]
    // - Contenido normal: z-[1-10]
    return "z-[9998]";
  };

  // Determinar las clases base del navbar
  const getBaseClasses = () => {
    const baseClasses = `
      ${staticPosition ? "relative" : "fixed"} 
      top-0 left-0 right-0 
      ${getZIndex()}
      transition-all duration-300 ease-in-out
      py-4
      w-full
    `;

    const backgroundClasses =
      isScrolled && !staticPosition
        ? "bg-white shadow-navbar"
        : staticPosition
        ? "bg-white shadow-sm"
        : "bg-transparent";

    return `${baseClasses} ${backgroundClasses} ${className}`.trim();
  };

  // Renderizar el contenido según el tipo
  const renderContent = () => {
    switch (type) {
      case "login":
        return <LoginNavbar />;

      case "new-order":
        return <NewOrderNavbar />;
      case "new-refills":
        return <NewRefillsNavbar />;

      case "general":
      default:
        return (
          <GeneralNavbar
            isScrolled={isScrolled || staticPosition}
            openSidebar={openSidebar}
          />
        );
    }
  };

  return (
    <>
      <nav className={getBaseClasses()}>{renderContent()}</nav>

      {/* Sidepanel - Solo para tipo general */}
      {type === "general" && activeSidebar && (
        <Sidepanel
          isOpen={!!activeSidebar}
          onClose={closeSidebar}
          type={activeSidebar}
        />
      )}
    </>
  );
};

export default Navbar;
